import React, { useEffect, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import { SessionHandler } from "verdi-web-client/APICommands/SessionHandler";

import { FilledSelect } from "@/components/filled-select/filled-select";
import { NAMESPACES } from "@/i18n/constants";
import FocusContext from "../../services/map-management/focus-context";
import { sortAndGroupAccounts } from "../../utils/sort-and-group-accounts";

export function AccountSelect({ label, showDefaultValue = true }) {
    const { t } = useTranslation(NAMESPACES.FEATURES.NAVIGATION, { keyPrefix: "account-select" });
    const defaultValue = t("select-account");
    const defaultState = useRef(defaultValue);

    // if the default value is shown, set the selected account to the default value else set it to an empty string so that MUI's label animation works
    const [selectedAccountId, setSelectedAccountId] = React.useState(showDefaultValue ? defaultValue : "");
    const onSelectAccount = (e) => {
        const value = e.target.value;
        FocusContext.startLoadHandler("Loading Account", "sessionLoad");
        SessionHandler.changeUser(value);
        setSelectedAccountId(value);
    };

    const sortAccounts = () => {
        if (!SessionHandler.accounts) {
            return;
        }

        SessionHandler.accounts.sort((a, b) => {
            if (a.name && b.name) {
                return a.name.localeCompare(b.name);
            }
            if (a.name) {
                return 1;
            }
            return -1;
        });
    };

    // Reset the default state on a language change (if the default is still selected)
    useEffect(() => {
        if (selectedAccountId === defaultState.current) {
            setSelectedAccountId(defaultValue);
            defaultState.current = defaultValue;
        }
    }, [defaultValue]);

    // Load the listeners for account
    useEffect(() => {
        const accountChangeListener = () => {
            sortAccounts();

            if (SessionHandler.currentUserObject && SessionHandler.currentUserObject.dbID !== selectedAccountId) {
                setSelectedAccountId(SessionHandler.currentUserObject.dbID);
            }
        };

        SessionHandler.onSessionLoad.addListener(accountChangeListener);

        return () => {
            SessionHandler.onSessionLoad.removeListener(accountChangeListener);
        };
    }, []);

    // Create the select options and subheaders for admin and user accounts
    const selectOptions = useMemo(() => {
        const availableAccounts = SessionHandler.accounts || [];
        const { customerAccounts, adminUseAccounts } = sortAndGroupAccounts(availableAccounts);
        // Headers are only relevant when required to disambiguate between admin and customer accounts
        const revealHeaders = customerAccounts.length > 0 && adminUseAccounts.length > 0;
        return [
            ...[
                ...(revealHeaders ? [{ id: "admin-header", name: "Admin-Use Only", subheader: true }] : []),
                ...adminUseAccounts.map((account) => ({
                    id: account.id,
                    value: account.id,
                    name: account.name,
                })),
            ],
            ...[
                ...(revealHeaders ? [{ id: "customer-accounts-header", name: "Customers", subheader: true }] : []),
                ...customerAccounts.map((account) => ({
                    id: account.id,
                    value: account.id,
                    name: account.name,
                })),
            ],
        ];
    }, [SessionHandler.accounts]);

    // Form control for selecting an account and separates admin and user accounts
    return (
        <FilledSelect
            labelId={"AdminAccountSelectorInputLabel"}
            defaultOptionLabel={showDefaultValue ? defaultValue : undefined} // show the default value if showDefaultValue is true
            selectOptions={selectOptions}
            onSelect={onSelectAccount}
            value={selectedAccountId}
            label={label}
            // Since this component lives in the TopNavbar, we override the default white bg style
            backgroundColor={"unset"}
            // This is so that in the case of a label, the highlight from MUI won't be shown
            inputLabelProps={{
                color: "dark",
            }}
            disableUnderline={true}
            formControlProps={{
                sx: {
                    height: "var(--nav-toolbar-height, 55px)",
                },
            }}
            selectProps={{
                sx: {
                    height: "var(--nav-toolbar-height, 55px)",
                },
            }}
        />
    );
}
